/**
 * Native (Android/iOS) app version, targetable inline in the Statsig console.
 *
 * Parsed from the webview User-Agent token `quince_app_<os>_<version>-<otaVersion>` (set by
 * ecomm-hybrid-app's `getUserAgent()`), e.g. `quince_app_ios_1.260317.0-r0` — on the SERVER from the
 * request UA (see `get-app-version-from-user-agent.ts`), on the CLIENT from `navigator.userAgent`.
 * Surfaced as the custom fields `app_version` (store binary version, e.g. `1.260317.0` — use
 * Statsig's `version` comparator) and `app_ota_version` (the OTA JS-bundle revision, e.g. `r0`).
 * Absent for web/mWeb, so nothing is added there.
 */
export interface StatsigAppVersion {
  version?: string
  otaVersion?: string
}

const APP_TOKEN_REGEX = /quince_app_(?:ios|android)_(\S+)/i

/**
 * Extract the app version from a webview User-Agent. Splits the token on its first `-` into the
 * store binary version (`app_version`) and the OTA revision (`app_ota_version`). Returns
 * `undefined` when the UA carries no app token (web/mWeb). Pure + isomorphic — shared by the server
 * (request UA) and client (`navigator.userAgent`) paths so both produce an identical custom shape.
 */
export function parseAppVersionFromUserAgent(userAgent: string | undefined): StatsigAppVersion | undefined {
  const token = userAgent?.match(APP_TOKEN_REGEX)?.[1]
  if (!token) return undefined

  const separatorIndex = token.indexOf('-')
  const version = separatorIndex === -1 ? token : token.slice(0, separatorIndex)
  const otaVersion = separatorIndex === -1 ? '' : token.slice(separatorIndex + 1)

  const appVersion: StatsigAppVersion = {}
  if (version) {
    appVersion.version = version
  }
  if (otaVersion) {
    appVersion.otaVersion = otaVersion
  }
  return appVersion.version === undefined && appVersion.otaVersion === undefined ? undefined : appVersion
}

export const buildStatsigCustom = ({
  isCountryCompliant,
  forcedBuckets,
  consentCookieValue,
  appVersion,
}: {
  isCountryCompliant: boolean
  forcedBuckets: Record<string, string | string[]>
  consentCookieValue: string | undefined
  appVersion?: StatsigAppVersion
}): Record<string, string | string[] | boolean> => {
  // Only add the app_* keys when actually present, so web/mWeb users (and any request whose UA
  // carries no app token) keep an app-version-free custom map.
  const appVersionCustom: Record<string, string> = {}
  if (appVersion?.version) {
    appVersionCustom.app_version = appVersion.version
  }
  if (appVersion?.otaVersion) {
    appVersionCustom.app_ota_version = appVersion.otaVersion
  }

  if (!isCountryCompliant) return { ...forcedBuckets, ...appVersionCustom }
  return { ...forcedBuckets, ...appVersionCustom, hasConsented: consentCookieValue === 'true' }
}
